/* HealthHub article enhancements (sections/vita-article-header.liquid, sections/vita-article-body.liquid).
 * Everything works without this file: the contents are anchor links in a <details>, Email is a mailto link.
 * - <vita-article-body>: opens the contents at 1100+ (and keeps them open there), marks the heading in
 *   view with aria-current, drives the reading progress bar, labels stacked table cells from the column
 *   heads, and publishes vita:article_toc_click { anchor, position }.
 * - Share: Copy link writes the URL to the clipboard, announces "Link copied" and publishes
 *   vita:share { method: 'copy_link' }; Email publishes vita:share { method: 'email' }.
 */
const track = (name, payload) => window.vitaTrack?.(name, payload);
const desktop = window.matchMedia('(min-width: 1100px)');

class VitaArticleBody extends HTMLElement {
  connectedCallback() {
    this.toc = this.querySelector('[data-vita-toc]');
    this.links = [...this.querySelectorAll('[data-vita-toc-link]')];
    this.progress = this.querySelector('[data-vita-progress]');
    this.main = this.querySelector('.vita-article-body__main');

    this.labelTables();
    if (!this.toc) return;

    this.syncOpen = () => {
      if (desktop.matches) this.toc.open = true;
    };
    this.syncOpen();
    desktop.addEventListener('change', this.syncOpen);
    // the summary is a label at 1100+: keep the list open
    this.toc.addEventListener('toggle', () => {
      if (desktop.matches && !this.toc.open) this.toc.open = true;
    });

    this.links.forEach((link, i) => {
      link.addEventListener('click', () => {
        track('article_toc_click', { anchor: link.hash.slice(1), position: i + 1 });
        if (!desktop.matches) this.toc.open = false;
      });
    });

    this.watchHeadings();
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateProgress();
      });
    };
    // capture: the page may scroll on an element rather than the window
    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    this.updateProgress();
  }

  disconnectedCallback() {
    desktop.removeEventListener('change', this.syncOpen);
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    this.observer?.disconnect();
  }

  watchHeadings() {
    const targets = this.links.map((link) => document.getElementById(link.hash.slice(1))).filter(Boolean);
    if (!targets.length || !('IntersectionObserver' in window)) return;
    const visible = new Set();
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
        const current = targets.find((t) => visible.has(t));
        if (current) this.setCurrent(current.id);
      },
      { rootMargin: '0px 0px -70% 0px' }
    );
    targets.forEach((t) => this.observer.observe(t));
  }

  setCurrent(id) {
    this.links.forEach((link) => {
      if (link.hash.slice(1) === id) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  }

  updateProgress() {
    if (!this.progress || !this.main) return;
    const rect = this.main.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    const done = total > 0 ? Math.min(Math.max(-rect.top / total, 0), 1) : rect.top < 0 ? 1 : 0;
    this.progress.style.setProperty('--vita-progress', done.toFixed(3));
    this.progress.setAttribute('aria-valuenow', String(Math.round(done * 100)));
  }

  // Below 750 table rows stack into cards; the CSS prints the column head before the third cell onwards
  labelTables() {
    this.querySelectorAll('.vita-article-body__content table').forEach((table) => {
      // The Shopify editor writes a plain table (no thead): the first row is the head, so promote it
      if (!table.querySelector('thead')) {
        const first = table.querySelector('tr');
        const rows = table.querySelectorAll('tr');
        if (first && rows.length > 1) {
          const thead = document.createElement('thead');
          thead.appendChild(first);
          table.insertBefore(thead, table.firstChild);
          if (!table.querySelector('tbody')) {
            const tbody = document.createElement('tbody');
            table.querySelectorAll(':scope > tr').forEach((tr) => tbody.appendChild(tr));
            table.appendChild(tbody);
          }
        }
      }
      const heads = [...table.querySelectorAll('thead th, thead td')].map((th) => th.textContent.trim());
      if (!heads.length) return;
      table.querySelectorAll('tbody tr').forEach((row) => {
        [...row.children].forEach((cell, i) => {
          if (heads[i] && !cell.hasAttribute('data-label')) cell.setAttribute('data-label', heads[i]);
        });
      });
    });
  }
}

if (!customElements.get('vita-article-body')) customElements.define('vita-article-body', VitaArticleBody);

if (!window.vitaArticleShare) {
  window.vitaArticleShare = true;
  document.addEventListener('click', async (e) => {
    const control = e.target.closest?.('[data-vita-share]');
    if (!control) return;
    const group = control.closest('[data-article-id]');
    const payload = { method: control.dataset.vitaShare, content_type: 'article', item_id: group?.dataset.articleId || '' };
    if (control.dataset.vitaShare === 'native' && navigator.share) {
      try {
        await navigator.share({ title: control.dataset.title || document.title, url: control.dataset.url || window.location.href });
        track('share', payload);
      } catch {
        // dismissed the share sheet: nothing was shared
      }
      return;
    }
    if (control.dataset.vitaShare === 'native') payload.method = 'copy_link';
    if (payload.method !== 'copy_link') {
      track('share', payload);
      return;
    }
    try {
      await navigator.clipboard.writeText(control.dataset.url || window.location.href);
    } catch {
      return;
    }
    const status = group?.querySelector('[data-vita-share-status]');
    const label = control.dataset.copiedLabel;
    if (status) status.textContent = label;
    const text = control.querySelector('[class*="share-text"]') || control;
    const original = text.textContent;
    text.textContent = label;
    setTimeout(() => {
      text.textContent = original;
      if (status) status.textContent = '';
    }, 2000);
    track('share', payload);
  });
}
